<template>
  <div
    data-component-group="form"
    data-component="edit-user"
    class="elevation-1"
  >
    <dialog-confirm
      v-if="blockWarning"
      class="block-sole-admin-warning"
      :is-on="true"
      :text="$t('block_sole_admin_warning_title')"
      :confirm-label="$t('block_sole_admin_warning_confirm')"
      :on-cancel="onBlockWarningCancel"
      :on-confirm="onBlockWarningConfirm"
    >
      <template v-slot:content>
        {{ $t('block_sole_admin_warning_text', { businesses: soleAdminBusinessNames }) }}
      </template>
    </dialog-confirm>

    <div class="component-body">
      <UserForm
        :user="user"
        confirm-trigger-label="Update user"
        submit-trigger-icon="account_circle"
        :is-disabled="!canEditDetails"
        :is-loading="isLoading"
        :is-cleared="isCleared"
        :on-submit="onSubmit"
        :on-change-role="onChangeRole"
      >
        <template v-slot:beforeForm>
          <Can action="approve-deny-user" subject="user">
            <DenyReasonForm
              v-if="isDenyReasonFormOn"
              @deny-reason="denyUser"
              @cancel-deny-reason="onToggleDenyReasonForm"
            />
          </Can>

          <div class="component-header">
            <Can action="approve-deny-user" subject="user">
              <div v-if="isPendingUser" class="pending-actions">
                <v-btn
                  class="bg-primary trigger allow-user"
                  :disabled="isDenyReasonFormOn"
                  @click="allowUser"
                >
                  <v-icon> thumb_up </v-icon>
                  {{ 'Allow' }}
                </v-btn>
                <v-btn
                  class="bg-secondary trigger deny-user"
                  :disabled="isDenyReasonFormOn"
                  @click="onToggleDenyReasonForm"
                >
                  <v-icon> thumb_down </v-icon>
                  {{ 'Deny' }}
                </v-btn>
              </div>
            </Can>

            <template v-if="canLogIntoAccount">
                <v-btn
                  v-if="true"
                  class="elevation-0 trigger log-into-account"
                  @click="logIntoUserAccount"
                  color="secondary"
                >
                  {{ 'Log into user account' }}
                </v-btn>
            </template>
          </div>

          <layout-section-header
            title="Details"
            :trigger-label="!canEditDetails ? 'Edit details' : 'Close'"
            @toggle="toggleEditDetails"
          />
        </template>
      </UserForm>

      <UserSettingsForm
        v-if="canViewSettings && settingsUnwrapped.length > 0"
        :settings="settingsUnwrapped"
        :on-submit="onUpdateSettings"
        :is-business-contact="isBusinessContact"
      >
        <template v-slot:beforeForm>
          <layout-section-header
            title="Settings"
            :has-trigger="false"
          />
        </template>
      </UserSettingsForm>

      <NotificationsForm
        v-if="canViewNotifications"
        :user="user"
        :is-disabled="!canEditNotifications"
      >
        <template v-slot:beforeForm>
          <layout-section-header
            title="Notifications"
            :trigger-label="
              !canEditNotifications ? 'Edit notifications' : 'Close'
            "
            @toggle="toggleEditNotifications"
          />
        </template>
      </NotificationsForm>
    </div>
  </div>
</template>

<script>
  import UserForm from './AddNew';
  import UserSettingsForm from './ProfileSettings';
  import NotificationsForm from './Notifications';
  import DenyReasonForm from './DenyReason';
  import { isArr, isObj, eachProp, hasProp } from '../../../utils';
  import defaultPagination from '../../../config/pagination';

  // TODO: Translate component

  // TODO: Extract deny-allow UI ('deny-reason-box', 'pending-actions') into a stand-alone component

  // TODO: Request that the api returns labels for users settings
  const settingLabels = {
    block_user: 'Block',
    is_business_admin: 'Business Admin',
    notif_user: 'Receive notifications',
  };

  export default {
    emits: ['saved'],
    components: {
      UserForm,
      UserSettingsForm,
      NotificationsForm,
      DenyReasonForm,
    },
    props: {
      isLoading: {
        type: Boolean,
        default: false,
      },
      isCleared: {
        type: Boolean,
        default: true,
      },
      onCancel: {
        type: Function,
        default: undefined,
      },
      onSubmit: {
        type: Function,
        default: undefined,
      },
    },
    data() {
      return {
        pagination: {
          ...defaultPagination,
        },
        canEditDetails: false,
        canUpdateUserStatus: true,
        isDenyReasonFormOn: false,
        canEditNotifications: false,
        hasNewRoleSelection: false,
        isBusinessContactSelected: false,
        settingsUnwrapped: [],
        blockWarning: null,
      };
    },
    computed: {
      user() {
        return this.$store.getters['users/activeItem'];
      },
      isPendingUser() {
        const { user } = this;
        return (
          isObj(user.status) &&
          user.status.name.toLowerCase() === 'pending review'
        );
      },
      isBusinessContact() {
        const { user, hasNewRoleSelection, isBusinessContactSelected, clear } =
          this;

        if (hasNewRoleSelection) {
          clear();
          return !!isBusinessContactSelected;
        }

        return (
          !!isObj(user.role) &&
          (user.role.slug === 'marketing_agent' || user.role.slug === 'business_contact' || user.role.slug === 'business_admin')
        );
      },
      isSuperAdmin() {
        const { user } = this;
        return isObj(user.role) && user.role.slug === 'super_admin';
      },
      settings() {
        const { settings } = this.$store.getters['users/activeItem'];
        return settings;
      },
      soleAdminBusinessNames() {
        const businesses = this.user.sole_admin_businesses;

        return isArr(businesses) ? businesses.map(({ name }) => name).join(', ') : '';
      },
      isVerified() {
        return this.user.verified === 1;
      },
      hasPermissions() {
        return isArr(this.user.permissions);
      },
      canLogIntoAccount() {
        return (
          this.hasPermissions &&
          (this.user.permissions.indexOf('am-can-log-into-this-profile') > -1 || this.user.permissions.indexOf('sa-can-log-into-this-profile') > -1) &&
          !this.isPendingUser &&
          this.isVerified
        );
      },
      canViewNotifications() {
        return (
          this.hasPermissions &&
          this.user.permissions.indexOf('manage-notifications') > -1
        );
      },
      canViewSettings() {
        return (
          this.hasPermissions &&
          this.user.permissions.indexOf('manage-settings') > -1
        );
      },
    },
    watch: {
      'user.id': {
        immediate: true,
        handler(nextId) {
          if (nextId) {
            this.canEditDetails = false;
            this.pagination = defaultPagination;

            this.getSettings(this.user);
          }
        }
      },
      settings: {
        handler(nextSettings) {
          this.unwrapSettings(nextSettings);
        },
        deep: true,
        immediate: true
      }
    },
    methods: {
      logIntoUserAccount() {
        return this.$store.dispatch('auth/logIntoAccount', this.user);
      },
      toggleEditDetails() {
        this.canEditDetails = !this.canEditDetails;
      },
      unwrapSettings(settings) {
        const nextSettings = [];

        eachProp(settings, (isOn, key) => {
          if (
            key === 'block_user' &&
            !this.$ability.can('change-status', 'user')
          ) {
            return false;
          }

          nextSettings.push({
            label: this.getSettingLabel(key),
            key,
            isOn: isOn > 0,
          });
        });

        this.settingsUnwrapped = nextSettings;
        return nextSettings;
      },
      wrapSettings() {
        const nextSettings = {};

        this.settingsUnwrapped.forEach(({ isOn, key }) => {
          if (
            key === 'block_user' &&
            !this.$ability.can('change-status', 'user')
          ) {
            return false;
          }

          nextSettings[key] = isOn ? 1 : 0;
        });

        return nextSettings;
      },
      onChangeRole(role) {
        this.hasNewRoleSelection = true;
        this.isBusinessContactSelected =
          role.slug === 'marketing_agent' || role.slug === 'business_contact' || role.slug === 'business_admin';
      },
      getSettingLabel(settingKey) {
        return hasProp(settingLabels, settingKey)
          ? settingLabels[settingKey]
          : settingKey;
      },
      getSettings({ id }) {
        const query = { id };

        return this.$store.dispatch('users/getSettings', query);
      },
      onUpdateSettings() {
        const { user, settings } = this;
        const convertedSettings = this.wrapSettings(settings);

        const query = {
          id: user.id,
          settings: convertedSettings,
        };

        return this.confirmBlockingSoleAdmin(convertedSettings)
          .then(() => this.$store.dispatch('users/updateSettings', query))
          .then((res) => {
            this.unwrapSettings(res);
          });
      },
      confirmBlockingSoleAdmin(nextSettings) {
        const isBlocking = nextSettings.block_user === 1 && !(this.settings?.block_user > 0);

        if (!isBlocking || !this.soleAdminBusinessNames) {
          return Promise.resolve();
        }

        return new Promise((resolve, reject) => {
          this.blockWarning = { resolve, reject };
        });
      },
      onBlockWarningConfirm() {
        this.blockWarning.resolve();
        this.blockWarning = null;
      },
      onBlockWarningCancel() {
        this.blockWarning.reject();
        this.blockWarning = null;
      },
      updateUserStatus(status = 0, reason = '') {
        const { user } = this;
        const { id, firstname, lastname } = user;

        const query = {
          reason,
          id,
          firstname,
          lastname,
          status,
        };

        return this.$store.dispatch('users/updateStatus', query).then((res) => {
          this.isDenyReasonFormOn = false;
          this.canUpdateUserStatus = false;
          const parentRoute = this.$route.matched[0]?.name || 'users';
          this.$emit('saved');
          this.$router.push({ name: parentRoute, query: this.$route.query });
          return res;
        });
      },
      denyUser(reason) {
        this.updateUserStatus(0, reason);
      },
      allowUser() {
        this.updateUserStatus(1);
      },
      onToggleDenyReasonForm() {
        this.isDenyReasonFormOn = !this.isDenyReasonFormOn;
      },
      toggleEditNotifications() {
        this.canEditNotifications = !this.canEditNotifications;
      },
      clear() {
        this.hasNewRoleSelection = false;
      },
    },
  };
</script>

<style lang="scss">
@use 'sass:map';
@use '../../../assets/styles/theme' as *;

[data-component-group='form'][data-component='edit-user'] {
  .component-header {
    position: relative;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
  }

  .component-body {
    opacity: 1;
    pointer-events: all;
    transition: all 0.15s ease-in-out;
  }

  .trigger.log-into-account,
  .trigger.allow-user,
  .trigger.deny-user {
    margin: 0;

    .v-icon {
      margin: 0 6px 0 0;
      font-size: $text-size-md;
    }

    &:before {
      background-color: map.get($colors, grey, base);
    }
  }

  .pending-actions {
    padding: 0 0 16px 0;
  }

  .trigger.allow-user {
    margin: 0 16px 0 0;
  }

  .trigger.log-into-account {
    margin: 0 0 16px 0;
  }
}
</style>
